export const extractImageSource = (backgroundValue) => {
  if (typeof backgroundValue !== "string") return "";
  const trimmedValue = backgroundValue.trim();
  if (!trimmedValue) return "";

  if (trimmedValue.startsWith("url(")) {
    const normalizedValue = trimmedValue
      .slice(4, -1)
      .trim()
      .replace(/^["']|["']$/g, "");
    return normalizedValue;
  }
  return trimmedValue;
};

export const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * ResizeObserver wrapper that runs callbacks on the next animation frame so
 * layout updates do not re-enter the observer in the same frame.
 */
export const createDeferredResizeObserver = (callback) => {
  if (typeof window === "undefined" || typeof window.ResizeObserver !== "function") {
    return null;
  }

  let frameId = 0;
  const observer = new window.ResizeObserver(() => {
    if (frameId) {
      window.cancelAnimationFrame(frameId);
    }
    frameId = window.requestAnimationFrame(() => {
      frameId = 0;
      callback();
    });
  });

  observer.cancelPendingFrame = () => {
    if (frameId) {
      window.cancelAnimationFrame(frameId);
      frameId = 0;
    }
  };

  return observer;
};

/** Slightly scales down the hit area modal preview while preserving aspect ratio. */
export const HIT_AREA_PREVIEW_SIZE_SCALE = 0.88;

/** Fit intrinsic image dimensions inside a max box without changing aspect ratio. */
export const computeAspectFitDimensions = (
  intrinsicWidth,
  intrinsicHeight,
  maxWidth,
  maxHeight
) => {
  const safeIntrinsicWidth = Math.max(1, Number(intrinsicWidth) || 1);
  const safeIntrinsicHeight = Math.max(1, Number(intrinsicHeight) || 1);
  const safeMaxWidth = Math.max(1, Number(maxWidth) || 1);
  const safeMaxHeight = Math.max(1, Number(maxHeight) || 1);
  const scale = Math.min(
    safeMaxWidth / safeIntrinsicWidth,
    safeMaxHeight / safeIntrinsicHeight
  );

  return {
    width: Math.max(1, Math.round(safeIntrinsicWidth * scale)),
    height: Math.max(1, Math.round(safeIntrinsicHeight * scale)),
  };
};
export const MIN_RECT_SIZE = 5;
export const HIT_AREA_DIMENSION_DECIMALS = 2;
export const MIN_TIMELINE_DURATION = 0.2;
export const HIT_AREA_TIMELINE_LEFT_INSET = 6;
export const HIT_AREA_TIMELINE_KEYFRAME_CLIP_PAD = 8;
export const HIT_AREA_TIMELINE_MARKER_HEAD_OVERHANG_PX = 6;
export const HIT_AREA_MODAL_TIMELINE_DURATION = 30;
export const HIT_AREA_TIMELINE_DEFAULT_VISIBLE_SECONDS = 15;
export const MAX_HIT_AREA_NAME_LENGTH = 30;
export const MAX_HIT_AREAS_PER_IMAGE = 10;
export const SNAP_TOLERANCE_PX = 3;
export const BOUNDARY_WARNING_THRESHOLD_PX = 5;
export const PROXIMITY_WARNING_THRESHOLD_PX = 12;
export const BOUNDARY_BADGE_HALF_WIDTH = 22;
export const BOUNDARY_BADGE_HALF_HEIGHT = 10;
export const BOUNDARY_BADGE_EDGE_INSET = 2;
export const MAX_SMART_FEEDBACK_CANDIDATES = 3;
export const KEYFRAME_TIME_PRECISION = 2;
export const KEYFRAME_TIME_EPSILON = 0.05;
export const KEYFRAME_DRAG_START_THRESHOLD_PX = 3;
export const KEYFRAME_POPUP_WIDTH = 362;
export const KEYFRAME_POPUP_MIN_EDGE_GAP = 8;
export const KEYFRAME_POPUP_ARROW_SAFE_GAP = 16;
export const KEYFRAME_POPUP_HORIZONTAL_ANCHOR_RATIO = 0.25;
export const KEYFRAME_POPUP_ESTIMATED_HEIGHT = 136;
export const KEYFRAME_POPUP_ARROW_SIZE = 14;
export const KEYFRAME_POPUP_MARKER_GAP = 8;
export const KEYFRAME_POPUP_TIMELINE_TITLE_HEIGHT = 26;
export const KEYFRAME_DUPLICATE_STEP_SECONDS = 1;
export const KEYFRAME_RECOMMENDED_MIN_SPACING_SECONDS = 0.2;
export const KEYFRAME_VISUAL_MIN_SPACING_PX = 12;
export const KEYFRAME_VISUAL_LANE_OFFSET_PX = 11;
export const HIT_AREA_LINK_PANEL_DESIRED_WIDTH = 280;
export const HIT_AREA_LINK_PANEL_MIN_WIDTH = 180;
export const HIT_AREA_LINK_PANEL_MIN_EDGE_GAP = 4;
export const HIT_AREA_LINK_PANEL_TOP_OFFSET_PX = 42;
export const HIT_AREA_LINK_PANEL_BOTTOM_OFFSET_PX = 8;
export const HIT_AREA_LIMIT_TOOLTIP =
  "Maximum 10 hit areas allowed per image. Delete one to add another.";
export const DEFAULT_HIT_AREA_URL_INPUT = "https://";

export const toNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

export const roundToInt = (value) => Math.round(Number(value) || 0);

export const roundDimension = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return parseFloat(parsed.toFixed(HIT_AREA_DIMENSION_DECIMALS));
};

export const formatDimensionForEditor = (value) => {
  if (value === "" || value === null || value === undefined) {
    return "";
  }
  const rounded = roundDimension(value);
  if (Number.isInteger(rounded)) {
    return String(rounded);
  }
  return String(rounded);
};

export const formatDimensionDisplay = (width, height) =>
  `${formatDimensionForEditor(width)} \u00D7 ${formatDimensionForEditor(height)}`;

export const normalizeStoredDimension = (value, { fromCanvasResize = false } = {}) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return fromCanvasResize ? roundToInt(parsed) : roundDimension(parsed);
};

/** Ignore sub-pixel canvas round-trip shrink while live-transforming on the canvas. */
export const persistCanvasDimension = (nextValue, existingValue) => {
  const normalizedNext = normalizeStoredDimension(nextValue, { fromCanvasResize: true });
  const normalizedExisting = normalizeStoredDimension(existingValue, {
    fromCanvasResize: false,
  });
  if (normalizedNext >= normalizedExisting) {
    return normalizedNext;
  }
  if (normalizedExisting - normalizedNext <= 1) {
    return normalizedExisting;
  }
  return normalizedNext;
};

export const roundTimelineTime = (value) =>
  Number(toNumber(value, 0).toFixed(KEYFRAME_TIME_PRECISION));

export const getTimelinePixelsPerSecond = (
  totalDuration = HIT_AREA_MODAL_TIMELINE_DURATION
) => (totalDuration < 60 ? 45 : 44.97);

export const getVisibleTimelineSecondsFromDraggerWidth = (
  draggerWidth,
  totalDuration = HIT_AREA_MODAL_TIMELINE_DURATION
) => {
  const pixelsPerSecond = getTimelinePixelsPerSecond(totalDuration);
  if (pixelsPerSecond <= 0) return 0;
  const rawSeconds = draggerWidth / pixelsPerSecond;
  if (Number.isInteger(rawSeconds)) return rawSeconds;
  return Math.round(rawSeconds * 10) / 10;
};

export const formatVisibleTimelineSeconds = (seconds) => {
  if (Number.isInteger(seconds)) return String(seconds);
  return Number(seconds).toFixed(1);
};

export const sortKeyframesByTime = (keyframes = []) =>
  [...keyframes].sort((firstFrame, secondFrame) => {
    if (firstFrame.time === secondFrame.time) {
      return String(firstFrame.id || "").localeCompare(String(secondFrame.id || ""));
    }
    return firstFrame.time - secondFrame.time;
  });

export const cloneHitAreaKeyframes = (keyframes = []) =>
  keyframes.map((frame, index) => ({
    ...frame,
    id: frame?.id || `hit-area-kf-${index + 1}`,
    time: Number(toNumber(frame?.time, 0).toFixed(2)),
    ...(frame?.width !== undefined ? { width: roundDimension(frame.width) } : {}),
    ...(frame?.height !== undefined ? { height: roundDimension(frame.height) } : {}),
    ...(frame?.x !== undefined ? { x: roundToInt(frame.x) } : {}),
    ...(frame?.y !== undefined ? { y: roundToInt(frame.y) } : {}),
  }));

export const sanitizeHitAreaKeyframes = (
  item,
  timelineStart,
  timelineEnd,
  fallbackWidth,
  fallbackHeight
) => {
  const rawKeyframes = Array.isArray(item?.keyframes)
    ? item.keyframes
    : Array.isArray(item?.keyFrames)
      ? item.keyFrames
      : [];

  const sanitizedKeyframes = rawKeyframes
    .map((frame, index) => {
      const frameTime = clamp(
        toNumber(frame?.time, toNumber(frame?.marker, timelineStart)),
        timelineStart,
        timelineEnd
      );
      return {
        id: frame?.id || `hit-area-kf-${index + 1}`,
        time: Number(frameTime.toFixed(2)),
        width: Math.max(
          MIN_RECT_SIZE,
          roundDimension(
            frame?.width !== undefined ? frame.width : fallbackWidth
          )
        ),
        height: Math.max(
          MIN_RECT_SIZE,
          roundDimension(
            frame?.height !== undefined ? frame.height : fallbackHeight
          )
        ),
        ...(frame?.x !== undefined ? { x: roundToInt(frame.x) } : {}),
        ...(frame?.y !== undefined ? { y: roundToInt(frame.y) } : {}),
      };
    })
    .filter((frame) => Number.isFinite(frame.time))
    .sort((firstFrame, secondFrame) => firstFrame.time - secondFrame.time);

  if (!sanitizedKeyframes.length) {
    return [];
  }

  return sanitizedKeyframes;
};

export const sanitizeHitArea = (
  item,
  index,
  imageWidth,
  imageHeight,
  totalDurationInSeconds,
  parentTimelineStart,
  parentTimelineEnd
) => {
  const rawImageWidth = toNumber(imageWidth, 0);
  const rawImageHeight = toNumber(imageHeight, 0);
  const hasValidImageSize =
    rawImageWidth >= MIN_RECT_SIZE && rawImageHeight >= MIN_RECT_SIZE;
  // When the parent image is temporarily 0x0 or below 5×5 (RHS / keyframe
  // typing), do not clamp hit-area geometry into a tiny box — that
  // permanently disturbs layout.
  const maxWidth = hasValidImageSize ? rawImageWidth : Math.max(1, toNumber(item?.width, 1));
  const maxHeight = hasValidImageSize
    ? rawImageHeight
    : Math.max(1, toNumber(item?.height, 1));
  const width = hasValidImageSize
    ? clamp(toNumber(item?.width, 0), 0, maxWidth)
    : toNumber(item?.width, 0);
  const height = hasValidImageSize
    ? clamp(toNumber(item?.height, 0), 0, maxHeight)
    : toNumber(item?.height, 0);
  const x = hasValidImageSize
    ? clamp(toNumber(item?.x, 0), 0, Math.max(0, maxWidth - width))
    : toNumber(item?.x, 0);
  const y = hasValidImageSize
    ? clamp(toNumber(item?.y, 0), 0, Math.max(0, maxHeight - height))
    : toNumber(item?.y, 0);
  const safeTotalDuration = Math.max(
    MIN_TIMELINE_DURATION,
    toNumber(totalDurationInSeconds, HIT_AREA_MODAL_TIMELINE_DURATION)
  );
  const safeParentStart = clamp(
    toNumber(parentTimelineStart, 0),
    0,
    safeTotalDuration - MIN_TIMELINE_DURATION
  );
  const safeParentEnd = clamp(
    toNumber(parentTimelineEnd, safeTotalDuration),
    safeParentStart + MIN_TIMELINE_DURATION,
    safeTotalDuration
  );
  const timelineStart = clamp(
    toNumber(item?.timelineStart, safeParentStart),
    safeParentStart,
    safeParentEnd - MIN_TIMELINE_DURATION
  );
  const timelineDurationRaw = toNumber(item?.timelineDuration, item?.durationInSeconds);
  const timelineEndValue = Number(item?.timelineEnd);
  const timelineEndFallback = Number.isFinite(timelineDurationRaw)
    ? timelineStart + timelineDurationRaw
    : safeParentEnd;
  const timelineEndUnclamped = Number.isFinite(timelineEndValue)
    ? timelineEndValue
    : timelineEndFallback;
  const timelineEnd = clamp(
    timelineEndUnclamped,
    timelineStart + MIN_TIMELINE_DURATION,
    safeParentEnd
  );
  const keyframes = sanitizeHitAreaKeyframes(
    item,
    timelineStart,
    timelineEnd,
    width,
    height
  );

  const defaultLabelIndex = resolveHitAreaDefaultLabelIndex(item);
  const trimmedName = String(item?.name ?? "").trim();
  const resolvedName =
    trimmedName ||
    (defaultLabelIndex > 0
      ? getHitAreaDefaultName(defaultLabelIndex)
      : getHitAreaDefaultName(index + 1));

  return {
    id: item?.id || `hit-area-${index + 1}`,
    name: resolvedName,
    defaultLabelIndex:
      defaultLabelIndex > 0
        ? defaultLabelIndex
        : trimmedName && !isDefaultHitAreaName(trimmedName)
          ? 0
          : index + 1,
    lockBoolean: !!item?.lockBoolean,
    hideBoolean: !!item?.hideBoolean,
    x: roundToInt(x),
    y: roundToInt(y),
    width: roundDimension(width),
    height: roundDimension(height),
    targetLink: isDefaultHitAreaUrlValue(item?.targetLink)
      ? ""
      : String(item?.targetLink ?? ""),
    timelineStart: Number(timelineStart.toFixed(2)),
    timelineEnd: Number(timelineEnd.toFixed(2)),
    keyframes: cloneHitAreaKeyframes(keyframes),
  };
};

/** Derive the image animation range from its timeline layer width (px). */
export const getImageTimelineRangeFromLayerWidth = (
  layerWidth,
  {
    totalDurationInSeconds = HIT_AREA_MODAL_TIMELINE_DURATION,
    totalDurationWidth,
    timelineStart: storedStart = 0,
    timelineEnd: storedEnd,
  } = {}
) => {
  const safeDuration = clamp(
    Math.round(Number(totalDurationInSeconds) || HIT_AREA_MODAL_TIMELINE_DURATION),
    1,
    90
  );
  const safeTimelineWidth = Math.max(
    200,
    Number(totalDurationWidth) ||
      safeDuration * getTimelinePixelsPerSecond(safeDuration)
  );
  const rowWidth = Number(layerWidth);
  const imageRowActivePercent =
    !Number.isFinite(rowWidth) || rowWidth <= 0 || safeTimelineWidth <= 0
      ? 82
      : clamp((rowWidth / safeTimelineWidth) * 100, 6, 100);
  const imageTimelineDuration = clamp(
    (imageRowActivePercent / 100) * safeDuration,
    MIN_TIMELINE_DURATION,
    safeDuration
  );
  const imageTimelineStart = clamp(
    toNumber(storedStart, 0),
    0,
    safeDuration - MIN_TIMELINE_DURATION
  );
  const fallbackEnd = imageTimelineStart + imageTimelineDuration;
  const imageTimelineEnd = clamp(
    toNumber(storedEnd, fallbackEnd),
    imageTimelineStart + MIN_TIMELINE_DURATION,
    safeDuration
  );

  return {
    timelineStart: roundTimelineTime(imageTimelineStart),
    timelineEnd: roundTimelineTime(imageTimelineEnd),
  };
};

/**
 * Remap hit-area ranges + keyframe times when the parent image animation
 * range grows or shrinks, keeping relative placement in sync.
 */
export const syncHitAreasToParentTimelineRange = (
  hitAreas = [],
  previousParentRange,
  nextParentRange,
  {
    imageWidth = 1,
    imageHeight = 1,
    totalDurationInSeconds = HIT_AREA_MODAL_TIMELINE_DURATION,
  } = {}
) => {
  if (!Array.isArray(hitAreas) || !hitAreas.length) return [];

  const oldStart = roundTimelineTime(
    toNumber(previousParentRange?.timelineStart, 0)
  );
  const oldEnd = roundTimelineTime(
    toNumber(
      previousParentRange?.timelineEnd,
      oldStart + MIN_TIMELINE_DURATION
    )
  );
  const newStart = roundTimelineTime(toNumber(nextParentRange?.timelineStart, 0));
  const newEnd = roundTimelineTime(
    toNumber(nextParentRange?.timelineEnd, newStart + MIN_TIMELINE_DURATION)
  );

  const oldDuration = Math.max(MIN_TIMELINE_DURATION, oldEnd - oldStart);
  const newDuration = Math.max(MIN_TIMELINE_DURATION, newEnd - newStart);

  const mapTime = (time, fallback) => {
    const source = roundTimelineTime(toNumber(time, fallback));
    const ratio = clamp((source - oldStart) / oldDuration, 0, 1);
    return roundTimelineTime(newStart + ratio * newDuration);
  };

  return hitAreas.map((item, index) => {
    const nextStart = mapTime(item?.timelineStart, oldStart);
    let nextEnd = mapTime(item?.timelineEnd, oldEnd);
    if (nextEnd < nextStart + MIN_TIMELINE_DURATION) {
      nextEnd = roundTimelineTime(
        Math.min(newEnd, nextStart + MIN_TIMELINE_DURATION)
      );
    }

    const mappedKeyframes = cloneHitAreaKeyframes(item?.keyframes || []).map(
      (frame) => ({
        ...frame,
        time: mapTime(frame.time, nextStart),
      })
    );

    return sanitizeHitArea(
      {
        ...item,
        timelineStart: nextStart,
        timelineEnd: nextEnd,
        keyframes: mappedKeyframes,
      },
      index,
      imageWidth,
      imageHeight,
      totalDurationInSeconds,
      newStart,
      newEnd
    );
  });
};

export const getKeyframeAtTime = (keyframes = [], time) => {
  const sortedFrames = sortKeyframesByTime(keyframes);
  if (!sortedFrames.length) return null;
  const targetTime = roundTimelineTime(time);
  let activeFrame = sortedFrames[0];
  sortedFrames.forEach((frame) => {
    if (frame.time <= targetTime + KEYFRAME_TIME_EPSILON) {
      activeFrame = frame;
    }
  });
  return activeFrame;
};

const hitAreaRectFromKeyframe = (keyframe, hitArea = {}) => ({
  x: roundToInt(keyframe?.x ?? hitArea?.x ?? 0),
  y: roundToInt(keyframe?.y ?? hitArea?.y ?? 0),
  width: Math.max(
    MIN_RECT_SIZE,
    roundDimension(keyframe?.width ?? hitArea?.width ?? MIN_RECT_SIZE)
  ),
  height: Math.max(
    MIN_RECT_SIZE,
    roundDimension(keyframe?.height ?? hitArea?.height ?? MIN_RECT_SIZE)
  ),
});

const clampHitAreaRectToImage = (rect, imageWidth, imageHeight) => {
  const safeImageWidth = toNumber(imageWidth, 0);
  const safeImageHeight = toNumber(imageHeight, 0);

  // Invalid/zero parent size: keep geometry as-is (rounded) so temporary 0x0
  // image dimensions do not crush hit areas.
  if (safeImageWidth <= 0 || safeImageHeight <= 0) {
    return {
      x: roundToInt(rect?.x ?? 0),
      y: roundToInt(rect?.y ?? 0),
      width: Math.max(MIN_RECT_SIZE, roundDimension(rect?.width ?? MIN_RECT_SIZE)),
      height: Math.max(MIN_RECT_SIZE, roundDimension(rect?.height ?? MIN_RECT_SIZE)),
    };
  }

  const width = clamp(
    roundDimension(rect?.width ?? MIN_RECT_SIZE),
    MIN_RECT_SIZE,
    Math.max(MIN_RECT_SIZE, safeImageWidth)
  );
  const height = clamp(
    roundDimension(rect?.height ?? MIN_RECT_SIZE),
    MIN_RECT_SIZE,
    Math.max(MIN_RECT_SIZE, safeImageHeight)
  );
  const x = clamp(roundToInt(rect?.x ?? 0), 0, Math.max(0, safeImageWidth - width));
  const y = clamp(roundToInt(rect?.y ?? 0), 0, Math.max(0, safeImageHeight - height));

  return {
    x,
    y,
    width: clamp(width, MIN_RECT_SIZE, Math.max(MIN_RECT_SIZE, safeImageWidth - x)),
    height: clamp(height, MIN_RECT_SIZE, Math.max(MIN_RECT_SIZE, safeImageHeight - y)),
  };
};

/**
 * True when scaling hit areas to the next image size would push any hit-area
 * (or keyframe) width/height below MIN_RECT_SIZE (5×5).
 */
export const wouldHitAreaScaleFallBelowMinSize = (
  hitAreas = [],
  previousWidth,
  previousHeight,
  nextWidth,
  nextHeight,
  minSize = MIN_RECT_SIZE
) => {
  const oldWidth = toNumber(previousWidth, 0);
  const oldHeight = toNumber(previousHeight, 0);
  const newWidth = toNumber(nextWidth, 0);
  const newHeight = toNumber(nextHeight, 0);

  if (oldWidth <= 0 || oldHeight <= 0 || newWidth <= 0 || newHeight <= 0) {
    return true;
  }

  // Image itself cannot host a minimum hit area without crushing layout.
  if (newWidth < minSize || newHeight < minSize) {
    return true;
  }

  const scaleX = newWidth / oldWidth;
  const scaleY = newHeight / oldHeight;

  return (hitAreas || []).some((item) => {
    const scaledWidth = toNumber(item?.width, minSize) * scaleX;
    const scaledHeight = toNumber(item?.height, minSize) * scaleY;
    if (scaledWidth < minSize || scaledHeight < minSize) {
      return true;
    }

    return (item?.keyframes || []).some((frame) => {
      if (
        frame?.width !== undefined &&
        toNumber(frame.width, minSize) * scaleX < minSize
      ) {
        return true;
      }
      if (
        frame?.height !== undefined &&
        toNumber(frame.height, minSize) * scaleY < minSize
      ) {
        return true;
      }
      return false;
    });
  });
};

/**
 * Scale hit-area geometry (and keyframe rects) when the parent image
 * width/height changes, keeping relative placement in sync.
 */
export const syncHitAreasToParentImageSize = (
  hitAreas = [],
  previousWidth,
  previousHeight,
  nextWidth,
  nextHeight
) => {
  if (!Array.isArray(hitAreas) || !hitAreas.length) return [];

  const oldWidthRaw = toNumber(previousWidth, 0);
  const oldHeightRaw = toNumber(previousHeight, 0);
  const newWidthRaw = toNumber(nextWidth, 0);
  const newHeightRaw = toNumber(nextHeight, 0);

  // Keep hit areas unchanged if either size is invalid/zero so temporary
  // 0 values during resize/typing do not collapse geometry.
  if (
    oldWidthRaw <= 0 ||
    oldHeightRaw <= 0 ||
    newWidthRaw <= 0 ||
    newHeightRaw <= 0
  ) {
    return hitAreas;
  }

  const oldWidth = oldWidthRaw;
  const oldHeight = oldHeightRaw;
  const newWidth = newWidthRaw;
  const newHeight = newHeightRaw;

  if (oldWidth === newWidth && oldHeight === newHeight) {
    return hitAreas;
  }

  // Do not disturb geometry when shrink would push any hit area below 5×5.
  if (
    wouldHitAreaScaleFallBelowMinSize(
      hitAreas,
      oldWidth,
      oldHeight,
      newWidth,
      newHeight
    )
  ) {
    return hitAreas;
  }

  const scaleX = newWidth / oldWidth;
  const scaleY = newHeight / oldHeight;

  return hitAreas.map((item) => {
    const scaledRect = clampHitAreaRectToImage(
      {
        x: toNumber(item?.x, 0) * scaleX,
        y: toNumber(item?.y, 0) * scaleY,
        width: toNumber(item?.width, MIN_RECT_SIZE) * scaleX,
        height: toNumber(item?.height, MIN_RECT_SIZE) * scaleY,
      },
      newWidth,
      newHeight
    );

    const scaledKeyframes = cloneHitAreaKeyframes(item?.keyframes || []).map(
      (frame) => {
        const nextFrame = { ...frame };
        if (frame?.x !== undefined || frame?.y !== undefined ||
            frame?.width !== undefined || frame?.height !== undefined) {
          const clampedFrame = clampHitAreaRectToImage(
            {
              x:
                frame?.x !== undefined
                  ? toNumber(frame.x, 0) * scaleX
                  : scaledRect.x,
              y:
                frame?.y !== undefined
                  ? toNumber(frame.y, 0) * scaleY
                  : scaledRect.y,
              width:
                frame?.width !== undefined
                  ? toNumber(frame.width, MIN_RECT_SIZE) * scaleX
                  : scaledRect.width,
              height:
                frame?.height !== undefined
                  ? toNumber(frame.height, MIN_RECT_SIZE) * scaleY
                  : scaledRect.height,
            },
            newWidth,
            newHeight
          );
          if (frame?.x !== undefined) nextFrame.x = clampedFrame.x;
          if (frame?.y !== undefined) nextFrame.y = clampedFrame.y;
          if (frame?.width !== undefined) nextFrame.width = clampedFrame.width;
          if (frame?.height !== undefined) {
            nextFrame.height = clampedFrame.height;
          }
        }
        return nextFrame;
      }
    );

    return {
      ...item,
      ...scaledRect,
      keyframes: scaledKeyframes,
    };
  });
};

/** Linearly interpolate hit-area rect between keyframes at a given time. */
export const getInterpolatedHitAreaRectAtTime = (
  hitArea,
  timeInSeconds,
  imageWidth,
  imageHeight
) => {
  if (!hitArea) return null;

  const fallbackRect = clampHitAreaRectToImage(
    hitAreaRectFromKeyframe(null, hitArea),
    imageWidth,
    imageHeight
  );
  const keyframes = sortKeyframesByTime(cloneHitAreaKeyframes(hitArea.keyframes || []));
  if (!keyframes.length) return fallbackRect;

  const targetTime = roundTimelineTime(timeInSeconds);
  const firstFrame = keyframes[0];
  const lastFrame = keyframes[keyframes.length - 1];

  if (targetTime <= firstFrame.time + KEYFRAME_TIME_EPSILON) {
    return clampHitAreaRectToImage(
      hitAreaRectFromKeyframe(firstFrame, hitArea),
      imageWidth,
      imageHeight
    );
  }

  if (targetTime >= lastFrame.time - KEYFRAME_TIME_EPSILON) {
    return clampHitAreaRectToImage(
      hitAreaRectFromKeyframe(lastFrame, hitArea),
      imageWidth,
      imageHeight
    );
  }

  for (let index = 0; index < keyframes.length - 1; index += 1) {
    const keyframeA = keyframes[index];
    const keyframeB = keyframes[index + 1];
    if (targetTime < keyframeA.time || targetTime > keyframeB.time) continue;

    const rectA = hitAreaRectFromKeyframe(keyframeA, hitArea);
    const rectB = hitAreaRectFromKeyframe(keyframeB, hitArea);
    const span = keyframeB.time - keyframeA.time;
    const progress = span <= 0 ? 0 : (targetTime - keyframeA.time) / span;

    return clampHitAreaRectToImage(
      {
        x: rectA.x + (rectB.x - rectA.x) * progress,
        y: rectA.y + (rectB.y - rectA.y) * progress,
        width: rectA.width + (rectB.width - rectA.width) * progress,
        height: rectA.height + (rectB.height - rectA.height) * progress,
      },
      imageWidth,
      imageHeight
    );
  }

  return clampHitAreaRectToImage(
    hitAreaRectFromKeyframe(lastFrame, hitArea),
    imageWidth,
    imageHeight
  );
};

/** Keyframe whose time exactly matches the playhead (not step-interpolated). */
export const getExactKeyframeAtTime = (
  keyframes = [],
  time,
  excludeKeyframeId = null
) => {
  const targetTime = roundTimelineTime(time);
  return (
    sortKeyframesByTime(keyframes).find((frame) => {
      if (
        excludeKeyframeId &&
        String(frame.id) === String(excludeKeyframeId)
      ) {
        return false;
      }
      return Math.abs(frame.time - targetTime) <= KEYFRAME_TIME_EPSILON;
    }) || null
  );
};

/** Update one keyframe by id; all other keyframes are shallow-cloned unchanged. */
export const patchKeyframeByIdOnly = (keyframes = [], targetId, patch = {}) => {
  if (targetId === undefined || targetId === null || targetId === "") {
    return cloneHitAreaKeyframes(keyframes);
  }

  let didPatch = false;
  const nextFrames = keyframes.map((frame) => {
    if (String(frame?.id ?? "") !== String(targetId)) {
      return { ...frame };
    }
    didPatch = true;
    return {
      ...frame,
      ...patch,
      id: frame.id,
      time: roundTimelineTime(frame.time),
    };
  });

  if (!didPatch) {
    return cloneHitAreaKeyframes(keyframes);
  }

  return cloneHitAreaKeyframes(nextFrames);
};

/** Append a new keyframe without modifying or deduping existing entries. */
export const appendHitAreaKeyframe = (keyframes = [], nextFrame) => {
  if (!nextFrame?.id) return cloneHitAreaKeyframes(keyframes);

  return sortKeyframesByTime([
    ...cloneHitAreaKeyframes(keyframes),
    {
      ...nextFrame,
      id: nextFrame.id,
      time: roundTimelineTime(nextFrame.time),
    },
  ]);
};

/** Pick the keyframe whose rect should be edited (prefer explicit selection). */
export const resolveKeyframeForPositionEdit = (
  keyframes = [],
  timeInSeconds,
  preferredKeyframeId = null,
  { allowStepFallback = false } = {}
) => {
  const sortedFrames = sortKeyframesByTime(keyframes);
  if (!sortedFrames.length) return null;

  const targetTime = roundTimelineTime(timeInSeconds);

  if (preferredKeyframeId) {
    const preferredFrame = sortedFrames.find(
      (frame) => String(frame.id) === String(preferredKeyframeId)
    );
    if (
      preferredFrame &&
      Math.abs(preferredFrame.time - targetTime) <= KEYFRAME_TIME_EPSILON
    ) {
      return preferredFrame;
    }
  }
  const exactFrame = sortedFrames.find(
    (frame) => Math.abs(frame.time - targetTime) <= KEYFRAME_TIME_EPSILON
  );
  if (exactFrame) return exactFrame;

  if (allowStepFallback) {
    return getKeyframeAtTime(sortedFrames, timeInSeconds);
  }

  return null;
};

/** Live canvas rect stored on the hit area (updated by drag/resize). */
export const getLiveHitAreaCanvasRect = (hitArea = {}) => ({
  x: roundToInt(hitArea?.x ?? 0),
  y: roundToInt(hitArea?.y ?? 0),
  width: Math.max(MIN_RECT_SIZE, roundDimension(hitArea?.width ?? MIN_RECT_SIZE)),
  height: Math.max(MIN_RECT_SIZE, roundDimension(hitArea?.height ?? MIN_RECT_SIZE)),
});

export const applyKeyframeEditorPreviewToRect = ({
  rect,
  hitArea,
  imageWidth,
  imageHeight,
  editorWidth,
  editorHeight,
  isPreviewActive,
  overlapObstacles = [],
}) => {
  if (!isPreviewActive || !rect || !hitArea || hitArea?.lockBoolean) {
    return rect;
  }

  const parsedWidth = Number(editorWidth);
  const parsedHeight = Number(editorHeight);
  if (!Number.isFinite(parsedWidth) || !Number.isFinite(parsedHeight)) {
    return rect;
  }

  const normalizedWidth = roundDimension(parsedWidth);
  const normalizedHeight = roundDimension(parsedHeight);
  if (normalizedWidth < MIN_RECT_SIZE || normalizedHeight < MIN_RECT_SIZE) {
    return rect;
  }

  const maxWidth = Math.max(MIN_RECT_SIZE, imageWidth - roundToInt(hitArea.x));
  const maxHeight = Math.max(MIN_RECT_SIZE, imageHeight - roundToInt(hitArea.y));
  const targetWidth = clamp(normalizedWidth, MIN_RECT_SIZE, maxWidth);
  const targetHeight = clamp(normalizedHeight, MIN_RECT_SIZE, maxHeight);

  if (!overlapObstacles.length) {
    return {
      ...rect,
      width: targetWidth,
      height: targetHeight,
    };
  }

  const constrainedRect = constrainRectSizeByInterpolation(
    { x: roundToInt(rect.x), y: roundToInt(rect.y) },
    { width: rect.width, height: rect.height },
    { width: targetWidth, height: targetHeight },
    overlapObstacles,
    imageWidth,
    imageHeight
  );

  return {
    ...rect,
    width: constrainedRect.width,
    height: constrainedRect.height,
  };
};

export const clampKeyframeEditorDimensionInput = (
  rawValue,
  { min = MIN_RECT_SIZE, max = MIN_RECT_SIZE } = {}
) => {
  if (rawValue === "" || rawValue === null || rawValue === undefined) {
    return rawValue;
  }

  let stringValue = String(rawValue).replace(/-/g, "").replace(/[^\d.]/g, "");
  const decimalParts = stringValue.split(".");
  if (decimalParts.length > 2) {
    stringValue = `${decimalParts[0]}.${decimalParts.slice(1).join("")}`;
  }

  if (stringValue.includes(".")) {
    const [wholePart, fractionPart = ""] = stringValue.split(".");
    stringValue = `${wholePart}.${fractionPart.slice(0, HIT_AREA_DIMENSION_DECIMALS)}`;
  }

  if (stringValue === "" || stringValue === ".") {
    return stringValue;
  }

  if (stringValue.endsWith(".")) {
    return stringValue;
  }

  const parsed = Number(stringValue);
  if (!Number.isFinite(parsed)) {
    return "";
  }

  if (parsed < min) {
    return stringValue;
  }

  const clamped = clamp(roundDimension(parsed), min, max);
  if (clamped !== roundDimension(parsed)) {
    return String(clamped);
  }

  return stringValue;
};

export const replaceOrInsertKeyframe = (keyframes = [], nextFrame) => {
  if (!nextFrame) return cloneHitAreaKeyframes(keyframes);

  const nextFrameId = nextFrame?.id;
  if (nextFrameId === undefined || nextFrameId === null || nextFrameId === "") {
    return cloneHitAreaKeyframes(keyframes);
  }

  const normalizedTime = roundTimelineTime(nextFrame.time);
  let hasReplaced = false;
  const updatedFrames = keyframes.map((frame) => {
    if (String(frame?.id ?? "") !== String(nextFrameId)) return frame;
    hasReplaced = true;
    return {
      ...frame,
      ...nextFrame,
      id: nextFrameId,
      time: normalizedTime,
    };
  });
  if (!hasReplaced) {
    updatedFrames.push({
      ...nextFrame,
      id: nextFrameId,
      time: normalizedTime,
    });
  }

  const deduped = [];
  sortKeyframesByTime(updatedFrames).forEach((frame) => {
    const alreadyExists = deduped.some(
      (item) => Math.abs(item.time - frame.time) <= KEYFRAME_TIME_EPSILON
    );
    if (!alreadyExists) {
      deduped.push(frame);
    }
  });
  return cloneHitAreaKeyframes(deduped);
};

export const isTimeWithinRange = (timeValue, rangeStart, rangeEnd) =>
  timeValue >= rangeStart - KEYFRAME_TIME_EPSILON &&
  timeValue <= rangeEnd + KEYFRAME_TIME_EPSILON;

export const clipKeyframesToRange = (
  keyframes = [],
  rangeStart,
  rangeEnd
) => {
  const normalizedStart = roundTimelineTime(rangeStart);
  const normalizedEnd = roundTimelineTime(rangeEnd);
  const orderedFrames = sortKeyframesByTime(cloneHitAreaKeyframes(keyframes));
  const inRangeFrames = orderedFrames
    .filter((frame) => isTimeWithinRange(frame.time, normalizedStart, normalizedEnd))
    .map((frame) => ({
      ...frame,
      time: roundTimelineTime(clamp(frame.time, normalizedStart, normalizedEnd)),
    }));

  let clippedFrames = sortKeyframesByTime(inRangeFrames);
  // Keep an empty keyframe list empty. Inventing a frame at range start
  // (often 0s) when resizing the hit-area layer bar was incorrect.
  if (!clippedFrames.length) {
    return [];
  }

  const dedupedFrames = [];
  clippedFrames.forEach((frame) => {
    const normalizedFrameTime = roundTimelineTime(frame.time);
    const hasTimeConflict = dedupedFrames.some(
      (item) => Math.abs(item.time - normalizedFrameTime) <= KEYFRAME_TIME_EPSILON
    );
    if (hasTimeConflict) return;
    dedupedFrames.push({
      ...frame,
      time: normalizedFrameTime,
      width: Math.max(MIN_RECT_SIZE, roundDimension(frame.width)),
      height: Math.max(MIN_RECT_SIZE, roundDimension(frame.height)),
    });
  });

  return cloneHitAreaKeyframes(dedupedFrames);
};

export const hasNearbyKeyframeSpacing = (
  keyframes = [],
  targetTime,
  excludeKeyframeId = null,
  minSpacingSeconds = KEYFRAME_RECOMMENDED_MIN_SPACING_SECONDS
) =>
  keyframes.some((frame) => {
    if (excludeKeyframeId && String(frame.id) === String(excludeKeyframeId)) {
      return false;
    }
    const delta = Math.abs(roundTimelineTime(frame.time) - roundTimelineTime(targetTime));
    return delta > KEYFRAME_TIME_EPSILON && delta < minSpacingSeconds;
  });

/**
 * Resolve a dragged keyframe time so it never overlaps / sits closer than
 * `minSpacingSeconds` to another keyframe. Entering another keyframe's
 * forbidden zone snaps to the near side; crossing its center jumps over it.
 */
export const constrainKeyframeDragTime = (
  intendedTime,
  {
    keyframes = [],
    draggedKeyframeId = null,
    initialTime = intendedTime,
    timelineStart = 0,
    timelineEnd = HIT_AREA_MODAL_TIMELINE_DURATION,
    minSpacingSeconds = KEYFRAME_RECOMMENDED_MIN_SPACING_SECONDS,
  } = {}
) => {
  const start = roundTimelineTime(timelineStart);
  const end = roundTimelineTime(timelineEnd);
  const origin = roundTimelineTime(initialTime);
  const intended = roundTimelineTime(intendedTime);
  let candidate = clamp(intended, start, end);

  const others = keyframes
    .filter(
      (frame) =>
        draggedKeyframeId == null ||
        String(frame.id) !== String(draggedKeyframeId)
    )
    .map((frame) => roundTimelineTime(frame.time))
    .sort((a, b) => a - b);

  if (!others.length) {
    return candidate;
  }

  // Forbidden open intervals (lo, hi): valid times are <= lo or >= hi.
  const rawIntervals = others.map((otherTime) => ({
    lo: roundTimelineTime(otherTime - minSpacingSeconds),
    hi: roundTimelineTime(otherTime + minSpacingSeconds),
  }));

  const merged = [];
  rawIntervals.forEach((interval) => {
    const last = merged[merged.length - 1];
    if (!last || interval.lo >= last.hi) {
      merged.push({ lo: interval.lo, hi: interval.hi });
      return;
    }
    last.hi = Math.max(last.hi, interval.hi);
  });

  const isInside = (time, cluster) => time > cluster.lo && time < cluster.hi;

  for (let guard = 0; guard < merged.length + 2; guard += 1) {
    const cluster = merged.find((item) => isInside(candidate, item));
    if (!cluster) {
      return candidate;
    }

    const mid = (cluster.lo + cluster.hi) / 2;
    const preferLeft =
      origin <= mid ? intended < mid : intended <= mid;
    const primary = preferLeft ? cluster.lo : cluster.hi;
    const secondary = preferLeft ? cluster.hi : cluster.lo;

    let next = clamp(roundTimelineTime(primary), start, end);
    if (isInside(next, cluster)) {
      next = clamp(roundTimelineTime(secondary), start, end);
    }
    if (isInside(next, cluster) || next === candidate) {
      return clamp(origin, start, end);
    }
    candidate = next;
  }

  return clamp(candidate, start, end);
};

export const getKeyframeLaneVerticalOffset = (laneIndex) => {
  if (!laneIndex || laneIndex <= 0) return 0;
  const step = Math.ceil(laneIndex / 2);
  const direction = laneIndex % 2 === 1 ? -1 : 1;
  return direction * step * KEYFRAME_VISUAL_LANE_OFFSET_PX;
};

export const buildKeyframeVisualLaneMap = (
  keyframes = [],
  timelineStart,
  timelineEnd,
  secondWidth,
  minSpacingPx = KEYFRAME_VISUAL_MIN_SPACING_PX
) => {
  const laneLastXPositions = [];
  const laneById = {};
  const safeDuration = Math.max(
    MIN_TIMELINE_DURATION,
    roundTimelineTime(timelineEnd - timelineStart)
  );
  const safeRangeWidth = Math.max(
    MIN_TIMELINE_DURATION * secondWidth,
    safeDuration * secondWidth
  );

  sortKeyframesByTime(keyframes).forEach((frame) => {
    const timeOffset = roundTimelineTime(frame.time - timelineStart);
    const xPosition = clamp(timeOffset * secondWidth, 0, safeRangeWidth);
    let laneIndex = laneLastXPositions.findIndex(
      (lastX) => xPosition - lastX >= minSpacingPx
    );
    if (laneIndex === -1) {
      laneIndex = laneLastXPositions.length;
      laneLastXPositions.push(xPosition);
    } else {
      laneLastXPositions[laneIndex] = xPosition;
    }
    laneById[String(frame.id)] = laneIndex;
  });

  return laneById;
};

export const createDimensionText = (width, height) =>
  formatDimensionDisplay(width, height);

export const createDisplayRect = (startPoint, endPoint) => ({
  x: Math.min(startPoint.x, endPoint.x),
  y: Math.min(startPoint.y, endPoint.y),
  width: Math.abs(endPoint.x - startPoint.x),
  height: Math.abs(endPoint.y - startPoint.y),
});

export const normalizeRect = (rect) => ({
  x: toNumber(rect?.x, 0),
  y: toNumber(rect?.y, 0),
  width: Math.max(0, toNumber(rect?.width, 0)),
  height: Math.max(0, toNumber(rect?.height, 0)),
});

export const getBoundaryDistances = (rect, canvasWidth, canvasHeight) => {
  const normalizedRect = normalizeRect(rect);
  const safeCanvasWidth = Math.max(1, toNumber(canvasWidth, 1));
  const safeCanvasHeight = Math.max(1, toNumber(canvasHeight, 1));
  const rightEdge = normalizedRect.x + normalizedRect.width;
  const bottomEdge = normalizedRect.y + normalizedRect.height;

  return {
    top: Math.max(0, normalizedRect.y),
    left: Math.max(0, normalizedRect.x),
    right: Math.max(0, safeCanvasWidth - rightEdge),
    bottom: Math.max(0, safeCanvasHeight - bottomEdge),
  };
};

export const resolveBoundaryBadgeCenter = ({
  side,
  displayRect,
  stageWidth,
  stageHeight,
}) => {
  const safeStageWidth = Math.max(1, toNumber(stageWidth, 1));
  const safeStageHeight = Math.max(1, toNumber(stageHeight, 1));
  const { x, y, width, height } = normalizeRect(displayRect);
  const centerX = x + width / 2;
  const centerY = y + height / 2;
  const minX = BOUNDARY_BADGE_HALF_WIDTH + BOUNDARY_BADGE_EDGE_INSET;
  const maxX = safeStageWidth - BOUNDARY_BADGE_HALF_WIDTH - BOUNDARY_BADGE_EDGE_INSET;
  const minY = BOUNDARY_BADGE_HALF_HEIGHT + BOUNDARY_BADGE_EDGE_INSET;
  const maxY = safeStageHeight - BOUNDARY_BADGE_HALF_HEIGHT - BOUNDARY_BADGE_EDGE_INSET;

  let preferredX = centerX;
  let preferredY = centerY;
  let alternateX;
  let alternateY;

  switch (side) {
    case "top":
      preferredX = centerX;
      preferredY = y - 18;
      alternateY = y + 18;
      break;
    case "left":
      preferredX = x - 26;
      preferredY = centerY;
      alternateX = x + 24;
      break;
    case "right":
      preferredX = x + width + 10;
      preferredY = centerY;
      alternateX = x + width - 24;
      break;
    case "bottom":
      preferredX = centerX;
      preferredY = y + height + 10;
      alternateY = y + height - 18;
      break;
    default:
      break;
  }

  let resolvedX = preferredX;
  let resolvedY = preferredY;

  if (resolvedX - BOUNDARY_BADGE_HALF_WIDTH < BOUNDARY_BADGE_EDGE_INSET) {
    resolvedX = alternateX ?? minX;
  } else if (
    resolvedX + BOUNDARY_BADGE_HALF_WIDTH >
    safeStageWidth - BOUNDARY_BADGE_EDGE_INSET
  ) {
    resolvedX = alternateX ?? maxX;
  }

  if (resolvedY - BOUNDARY_BADGE_HALF_HEIGHT < BOUNDARY_BADGE_EDGE_INSET) {
    resolvedY = alternateY ?? minY;
  } else if (
    resolvedY + BOUNDARY_BADGE_HALF_HEIGHT >
    safeStageHeight - BOUNDARY_BADGE_EDGE_INSET
  ) {
    resolvedY = alternateY ?? maxY;
  }

  return {
    x: clamp(resolvedX, minX, maxX),
    y: clamp(resolvedY, minY, maxY),
  };
};

export const isRectNearBoundary = (
  rect,
  canvasWidth,
  canvasHeight,
  threshold = BOUNDARY_WARNING_THRESHOLD_PX
) => {
  const distances = getBoundaryDistances(rect, canvasWidth, canvasHeight);
  return (
    distances.top < threshold ||
    distances.left < threshold ||
    distances.right < threshold ||
    distances.bottom < threshold
  );
};

export const rectanglesOverlap = (firstRect, secondRect) => {
  const first = normalizeRect(firstRect);
  const second = normalizeRect(secondRect);

  if (
    first.width <= 0 ||
    first.height <= 0 ||
    second.width <= 0 ||
    second.height <= 0
  ) {
    return false;
  }

  return (
    first.x < second.x + second.width &&
    first.x + first.width > second.x &&
    first.y < second.y + second.height &&
    first.y + first.height > second.y
  );
};

export const calculateRectDistance = (firstRect, secondRect) => {
  const first = normalizeRect(firstRect);
  const second = normalizeRect(secondRect);
  const deltaX = Math.max(
    second.x - (first.x + first.width),
    first.x - (second.x + second.width),
    0
  );
  const deltaY = Math.max(
    second.y - (first.y + first.height),
    first.y - (second.y + second.height),
    0
  );
  return Math.hypot(deltaX, deltaY);
};

/** Smallest edge-to-edge gap between two rects; 0 when overlapping or touching. */
export const getRectEdgeGap = (firstRect, secondRect) => {
  const first = normalizeRect(firstRect);
  const second = normalizeRect(secondRect);

  if (rectanglesOverlap(first, second)) {
    return 0;
  }

  const firstRight = first.x + first.width;
  const firstBottom = first.y + first.height;
  const secondRight = second.x + second.width;
  const secondBottom = second.y + second.height;
  const edgeGaps = [];

  const overlapTop = Math.max(first.y, second.y);
  const overlapBottom = Math.min(firstBottom, secondBottom);
  if (overlapBottom > overlapTop) {
    if (firstRight <= second.x) {
      edgeGaps.push(second.x - firstRight);
    } else if (secondRight <= first.x) {
      edgeGaps.push(first.x - secondRight);
    }
  }

  const overlapLeft = Math.max(first.x, second.x);
  const overlapRight = Math.min(firstRight, secondRight);
  if (overlapRight > overlapLeft) {
    if (firstBottom <= second.y) {
      edgeGaps.push(second.y - firstBottom);
    } else if (secondBottom <= first.y) {
      edgeGaps.push(first.y - secondBottom);
    }
  }

  if (!edgeGaps.length) {
    return calculateRectDistance(first, second);
  }

  return Math.min(...edgeGaps);
};

export const isRectNearOtherRects = (
  rect,
  otherRects = [],
  threshold = BOUNDARY_WARNING_THRESHOLD_PX
) =>
  otherRects.some(
    (otherRect) => getRectEdgeGap(rect, otherRect) < threshold
  );

export const interpolateRect = (fromRect, toRect, progress) => ({
  x: fromRect.x + (toRect.x - fromRect.x) * progress,
  y: fromRect.y + (toRect.y - fromRect.y) * progress,
  width: fromRect.width + (toRect.width - fromRect.width) * progress,
  height: fromRect.height + (toRect.height - fromRect.height) * progress,
});

export const rectOverlapsAnyObstacle = (candidateRect, obstacles = []) =>
  obstacles.some((obstacle) => rectanglesOverlap(candidateRect, obstacle));

const normalizeConstrainedRect = (rect) => ({
  x: roundToInt(rect?.x ?? 0),
  y: roundToInt(rect?.y ?? 0),
  width: Math.max(MIN_RECT_SIZE, roundDimension(rect?.width ?? MIN_RECT_SIZE)),
  height: Math.max(MIN_RECT_SIZE, roundDimension(rect?.height ?? MIN_RECT_SIZE)),
});

/** Binary-search from fallback to target so the result touches but does not overlap obstacles. */
export const constrainRectToNonOverlapByInterpolation = (
  targetRect,
  fallbackRect,
  obstacles = [],
  { iterations = 18 } = {}
) => {
  const target = normalizeRect(targetRect);
  if (target.width <= 0 || target.height <= 0) {
    return null;
  }

  if (!rectOverlapsAnyObstacle(target, obstacles)) {
    return normalizeConstrainedRect(target);
  }

  const fallback = normalizeRect(fallbackRect);
  if (fallback.width <= 0 || fallback.height <= 0) {
    return null;
  }

  if (rectOverlapsAnyObstacle(fallback, obstacles)) {
    return normalizeConstrainedRect(fallback);
  }

  let low = 0;
  let high = 1;
  let bestRect = fallback;

  for (let iteration = 0; iteration < iterations; iteration += 1) {
    const middle = (low + high) / 2;
    const probeRect = interpolateRect(fallback, target, middle);
    if (rectOverlapsAnyObstacle(probeRect, obstacles)) {
      high = middle;
      continue;
    }

    low = middle;
    bestRect = probeRect;
  }

  return normalizeConstrainedRect(bestRect);
};

const clampRectToBounds = (rect, boundsWidth, boundsHeight, minSize = MIN_RECT_SIZE) => {
  const width = Math.max(minSize, toNumber(rect?.width, minSize));
  const height = Math.max(minSize, toNumber(rect?.height, minSize));
  const maxX = Math.max(0, boundsWidth - width);
  const maxY = Math.max(0, boundsHeight - height);

  return {
    x: clamp(toNumber(rect?.x, 0), 0, maxX),
    y: clamp(toNumber(rect?.y, 0), 0, maxY),
    width,
    height,
  };
};

/** Push a moving rect to the nearest obstacle edge along the drag direction. */
const clampMoveRectToObstacleBoundaries = (rect, fallback, intent, obstacles = []) => {
  let { x, y, width, height } = normalizeRect(rect);
  const from = normalizeRect(fallback);
  const toward = normalizeRect(intent);
  const deltaX = toward.x - from.x;
  const deltaY = toward.y - from.y;

  obstacles.forEach((obstacle) => {
    const obs = normalizeRect(obstacle);
    if (obs.width <= 0 || obs.height <= 0) return;
    if (!rectanglesOverlap({ x, y, width, height }, obs)) return;

    if (deltaX > 0) {
      x = Math.min(x, obs.x - width);
    } else if (deltaX < 0) {
      x = Math.max(x, obs.x + obs.width);
    }

    if (deltaY > 0) {
      y = Math.min(y, obs.y - height);
    } else if (deltaY < 0) {
      y = Math.max(y, obs.y + obs.height);
    }

    if (deltaX === 0 && rectanglesOverlap({ x, y, width, height }, obs)) {
      const centerX = x + width / 2;
      const obsCenterX = obs.x + obs.width / 2;
      if (centerX <= obsCenterX) {
        x = Math.min(x, obs.x - width);
      } else {
        x = Math.max(x, obs.x + obs.width);
      }
    }

    if (deltaY === 0 && rectanglesOverlap({ x, y, width, height }, obs)) {
      const centerY = y + height / 2;
      const obsCenterY = obs.y + obs.height / 2;
      if (centerY <= obsCenterY) {
        y = Math.min(y, obs.y - height);
      } else {
        y = Math.max(y, obs.y + obs.height);
      }
    }
  });

  return { x, y, width, height };
};

const constrainRectMoveAlongVector = (
  candidateRect,
  fallbackRect,
  obstacles = [],
  boundsWidth,
  boundsHeight,
  clampRectFn,
  { iterations = 24 } = {}
) => {
  const fallback = clampRectFn(normalizeRect(fallbackRect), boundsWidth, boundsHeight);
  const intent = clampRectFn(normalizeRect(candidateRect), boundsWidth, boundsHeight);

  if (intent.width <= 0 || intent.height <= 0) {
    return fallback;
  }

  if (!rectOverlapsAnyObstacle(intent, obstacles)) {
    return intent;
  }

  if (fallback.width <= 0 || fallback.height <= 0) {
    return null;
  }

  if (rectOverlapsAnyObstacle(fallback, obstacles)) {
    return fallback;
  }

  const deltaX = intent.x - fallback.x;
  const deltaY = intent.y - fallback.y;
  let low = 0;
  let high = 1;
  let bestRect = fallback;

  for (let index = 0; index < iterations; index += 1) {
    const middle = (low + high) / 2;
    const probeRect = clampRectFn(
      {
        x: fallback.x + deltaX * middle,
        y: fallback.y + deltaY * middle,
        width: intent.width,
        height: intent.height,
      },
      boundsWidth,
      boundsHeight
    );

    if (rectOverlapsAnyObstacle(probeRect, obstacles)) {
      high = middle;
      continue;
    }

    low = middle;
    bestRect = probeRect;
  }

  bestRect = clampMoveRectToObstacleBoundaries(bestRect, fallback, intent, obstacles);
  bestRect = clampRectFn(bestRect, boundsWidth, boundsHeight);

  if (rectOverlapsAnyObstacle(bestRect, obstacles)) {
    return fallback;
  }

  return bestRect;
};

/** Clamp a move drag so the rect slides until it touches obstacles without overlapping. */
export const constrainRectMoveToNonOverlap = (
  candidateRect,
  fallbackRect,
  obstacles = [],
  imageWidth,
  imageHeight,
  options = {}
) => {
  const resolved = constrainRectMoveAlongVector(
    candidateRect,
    fallbackRect,
    obstacles,
    imageWidth,
    imageHeight,
    (rect, boundsWidth, boundsHeight) =>
      clampHitAreaRectToImage(rect, boundsWidth, boundsHeight),
    options
  );

  if (!resolved) {
    return null;
  }

  return normalizeConstrainedRect(resolved);
};

/** Display-space move constraint for live canvas drag (avoids image round-trip drift). */
export const constrainDisplayRectMoveToNonOverlap = (
  candidateRect,
  fallbackRect,
  obstacles = [],
  stageWidth,
  stageHeight,
  { anchorRect = null } = {}
) => {
  const fallback = clampRectToBounds(normalizeRect(fallbackRect), stageWidth, stageHeight);
  const anchor = clampRectToBounds(
    normalizeRect(anchorRect || fallbackRect),
    stageWidth,
    stageHeight
  );
  const width = Math.max(MIN_RECT_SIZE, toNumber(candidateRect?.width, fallback.width));
  const height = Math.max(MIN_RECT_SIZE, toNumber(candidateRect?.height, fallback.height));
  const intentX = toNumber(candidateRect?.x, fallback.x);
  const intentY = toNumber(candidateRect?.y, fallback.y);
  const dx = intentX - anchor.x;
  const dy = intentY - anchor.y;

  let x = clamp(intentX, 0, Math.max(0, stageWidth - width));
  let y = clamp(intentY, 0, Math.max(0, stageHeight - height));
  const intent = { x, y, width, height };

  if (!rectOverlapsAnyObstacle(intent, obstacles)) {
    return intent;
  }

  if (fallback.width <= 0 || fallback.height <= 0) {
    return null;
  }

  if (rectOverlapsAnyObstacle(fallback, obstacles)) {
    return fallback;
  }

  const slid = constrainRectMoveAlongVector(
    candidateRect,
    fallbackRect,
    obstacles,
    stageWidth,
    stageHeight,
    (rect, boundsWidth, boundsHeight) =>
      clampRectToBounds(rect, boundsWidth, boundsHeight)
  );
  if (slid && !rectOverlapsAnyObstacle(slid, obstacles)) {
    return slid;
  }

  let minX = 0;
  let maxX = Math.max(0, stageWidth - width);
  let minY = 0;
  let maxY = Math.max(0, stageHeight - height);

  obstacles.forEach((obstacle) => {
    const obs = normalizeRect(obstacle);
    if (obs.width <= 0 || obs.height <= 0) return;

    const verticalBandOverlap =
      y < obs.y + obs.height && y + height > obs.y;
    const horizontalBandOverlap =
      x < obs.x + obs.width && x + width > obs.x;

    if (verticalBandOverlap) {
      if (dx > 0) {
        maxX = Math.min(maxX, obs.x - width);
      } else if (dx < 0) {
        minX = Math.max(minX, obs.x + obs.width);
      } else if (anchor.x + width <= obs.x) {
        maxX = Math.min(maxX, obs.x - width);
      } else if (anchor.x >= obs.x + obs.width) {
        minX = Math.max(minX, obs.x + obs.width);
      } else if (intentX + width / 2 <= obs.x + obs.width / 2) {
        maxX = Math.min(maxX, obs.x - width);
      } else {
        minX = Math.max(minX, obs.x + obs.width);
      }
    }

    if (horizontalBandOverlap) {
      if (dy > 0) {
        maxY = Math.min(maxY, obs.y - height);
      } else if (dy < 0) {
        minY = Math.max(minY, obs.y + obs.height);
      } else if (anchor.y + height <= obs.y) {
        maxY = Math.min(maxY, obs.y - height);
      } else if (anchor.y >= obs.y + obs.height) {
        minY = Math.max(minY, obs.y + obs.height);
      } else if (intentY + height / 2 <= obs.y + obs.height / 2) {
        maxY = Math.min(maxY, obs.y - height);
      } else {
        minY = Math.max(minY, obs.y + obs.height);
      }
    }
  });

  if (minX <= maxX) {
    x = clamp(intentX, minX, maxX);
  } else {
    x = fallback.x;
  }

  if (minY <= maxY) {
    y = clamp(intentY, minY, maxY);
  } else {
    y = fallback.y;
  }

  let resolved = clampRectToBounds({ x, y, width, height }, stageWidth, stageHeight);

  for (let pass = 0; pass < Math.max(obstacles.length, 1); pass += 1) {
    let adjusted = false;
    obstacles.forEach((obstacle) => {
      const obs = normalizeRect(obstacle);
      if (!rectanglesOverlap(resolved, obs)) return;

      if (dx > 0) {
        resolved = { ...resolved, x: Math.min(resolved.x, obs.x - width) };
        adjusted = true;
      } else if (dx < 0) {
        resolved = { ...resolved, x: Math.max(resolved.x, obs.x + obs.width) };
        adjusted = true;
      }

      if (dy > 0) {
        resolved = { ...resolved, y: Math.min(resolved.y, obs.y - height) };
        adjusted = true;
      } else if (dy < 0) {
        resolved = { ...resolved, y: Math.max(resolved.y, obs.y + obs.height) };
        adjusted = true;
      }

      if (dx === 0 && dy === 0) {
        if (fallback.x + width <= obs.x) {
          resolved = { ...resolved, x: Math.min(resolved.x, obs.x - width) };
        } else if (fallback.x >= obs.x + obs.width) {
          resolved = { ...resolved, x: Math.max(resolved.x, obs.x + obs.width) };
        }
        if (fallback.y + height <= obs.y) {
          resolved = { ...resolved, y: Math.min(resolved.y, obs.y - height) };
        } else if (fallback.y >= obs.y + obs.height) {
          resolved = { ...resolved, y: Math.max(resolved.y, obs.y + obs.height) };
        }
        adjusted = true;
      }
    });
    resolved = clampRectToBounds(resolved, stageWidth, stageHeight);
    if (!adjusted) break;
  }

  if (rectOverlapsAnyObstacle(resolved, obstacles)) {
    return fallback;
  }

  return resolved;
};

/** Display-space resize with a fixed anchor corner from drag start (no image round-trip). */
export const constrainDisplayRectResizeToNonOverlap = (
  candidateRect,
  startRect,
  obstacles = [],
  stageWidth,
  stageHeight,
  direction = ""
) => {
  const start = clampRectToBounds(normalizeRect(startRect), stageWidth, stageHeight);
  const dir = String(direction).toLowerCase();
  const growLeft = dir.includes("left");
  const growRight = dir.includes("right");
  const growTop = dir.includes("top");
  const growBottom = dir.includes("bottom");

  const fixedLeft = start.x;
  const fixedTop = start.y;
  const fixedRight = start.x + start.width;
  const fixedBottom = start.y + start.height;
  const candidate = normalizeRect(candidateRect);

  let left = growLeft ? toNumber(candidate.x, fixedLeft) : fixedLeft;
  let top = growTop ? toNumber(candidate.y, fixedTop) : fixedTop;
  let right = growRight
    ? toNumber(candidate.x, fixedLeft) +
      Math.max(MIN_RECT_SIZE, toNumber(candidate.width, start.width))
    : fixedRight;
  let bottom = growBottom
    ? toNumber(candidate.y, fixedTop) +
      Math.max(MIN_RECT_SIZE, toNumber(candidate.height, start.height))
    : fixedBottom;

  const lockFixedEdges = () => {
    if (!growLeft) left = fixedLeft;
    if (!growRight) right = fixedRight;
    if (!growTop) top = fixedTop;
    if (!growBottom) bottom = fixedBottom;
  };

  const enforceMinSize = () => {
    if (right - left < MIN_RECT_SIZE) {
      if (growLeft && !growRight) {
        left = right - MIN_RECT_SIZE;
      } else if (growRight && !growLeft) {
        right = left + MIN_RECT_SIZE;
      }
    }
    if (bottom - top < MIN_RECT_SIZE) {
      if (growTop && !growBottom) {
        top = bottom - MIN_RECT_SIZE;
      } else if (growBottom && !growTop) {
        bottom = top + MIN_RECT_SIZE;
      }
    }
  };

  const clampToStage = () => {
    if (growLeft) {
      left = clamp(left, 0, fixedRight - MIN_RECT_SIZE);
    }
    if (growRight) {
      right = clamp(right, fixedLeft + MIN_RECT_SIZE, stageWidth);
    }
    if (growTop) {
      top = clamp(top, 0, fixedBottom - MIN_RECT_SIZE);
    }
    if (growBottom) {
      bottom = clamp(bottom, fixedTop + MIN_RECT_SIZE, stageHeight);
    }
    lockFixedEdges();
  };

  const applyObstacleLimits = () => {
    let changed = false;

    obstacles.forEach((obstacle) => {
      const obs = normalizeRect(obstacle);
      if (obs.width <= 0 || obs.height <= 0) return;

      const verticalOverlap = top < obs.y + obs.height && bottom > obs.y;
      const horizontalOverlap = left < obs.x + obs.width && right > obs.x;
      const obstacleIsAbove = obs.y + obs.height <= fixedTop;
      const obstacleIsBelow = obs.y >= fixedBottom;
      const obstacleIsLeft = obs.x + obs.width <= fixedLeft;
      const obstacleIsRight = obs.x >= fixedRight;

      if (growRight && verticalOverlap && obstacleIsRight) {
        const cappedRight = Math.min(right, obs.x);
        if (cappedRight < right) {
          right = cappedRight;
          changed = true;
        }
      }

      if (growLeft && verticalOverlap && obstacleIsLeft) {
        const cappedLeft = Math.max(left, obs.x + obs.width);
        if (cappedLeft > left) {
          left = cappedLeft;
          changed = true;
        }
      }

      if (growBottom && horizontalOverlap && obstacleIsBelow) {
        const cappedBottom = Math.min(bottom, obs.y);
        if (cappedBottom < bottom) {
          bottom = cappedBottom;
          changed = true;
        }
      }

      if (growTop && horizontalOverlap && obstacleIsAbove) {
        const cappedTop = Math.max(top, obs.y + obs.height);
        if (cappedTop > top) {
          top = cappedTop;
          changed = true;
        }
      }
    });

    return changed;
  };

  lockFixedEdges();
  clampToStage();
  enforceMinSize();

  for (let pass = 0; pass < Math.max(obstacles.length + 1, 2); pass += 1) {
    const changed = applyObstacleLimits();
    lockFixedEdges();
    enforceMinSize();
    clampToStage();
    if (!changed) break;
  }

  lockFixedEdges();
  enforceMinSize();

  return {
    x: left,
    y: top,
    width: Math.max(MIN_RECT_SIZE, right - left),
    height: Math.max(MIN_RECT_SIZE, bottom - top),
  };
};

/** Clamp a resize drag so the rect can grow only until it touches obstacles. */
export const constrainRectResizeToNonOverlap = (
  candidateRect,
  fallbackRect,
  obstacles = [],
  direction = "",
  imageWidth,
  imageHeight
) => {
  const fallback = clampHitAreaRectToImage(
    normalizeRect(fallbackRect),
    imageWidth,
    imageHeight
  );
  let rect = clampHitAreaRectToImage(normalizeRect(candidateRect), imageWidth, imageHeight);
  const dir = String(direction).toLowerCase();

  const growingRight = dir.includes("right");
  const growingLeft = dir.includes("left");
  const growingBottom = dir.includes("bottom");
  const growingTop = dir.includes("top");
  const anchorRight = rect.x + rect.width;
  const anchorBottom = rect.y + rect.height;

  const obstacleBlocksRightGrowth = (obs) =>
    obs.x >= rect.x + MIN_RECT_SIZE;

  const obstacleBlocksLeftGrowth = (obs) =>
    obs.x + obs.width <= fallback.x ||
    (obs.x + obs.width <= anchorRight && obs.x < rect.x);

  const obstacleBlocksBottomGrowth = (obs) =>
    obs.y >= rect.y + MIN_RECT_SIZE;

  const obstacleBlocksTopGrowth = (obs) =>
    obs.y + obs.height <= fallback.y ||
    (obs.y + obs.height <= anchorBottom && obs.y < rect.y);

  const applyObstacleLimits = () => {
    let changed = false;

    obstacles.forEach((obstacle) => {
      const obs = normalizeRect(obstacle);
      if (obs.width <= 0 || obs.height <= 0) return;

      const verticalOverlap = rect.y < obs.y + obs.height && rect.y + rect.height > obs.y;
      const horizontalOverlap =
        rect.x < obs.x + obs.width && rect.x + rect.width > obs.x;

      if (
        growingRight &&
        verticalOverlap &&
        obstacleBlocksRightGrowth(obs)
      ) {
        const maxWidth = Math.max(MIN_RECT_SIZE, obs.x - rect.x);
        if (rect.width > maxWidth) {
          rect.width = maxWidth;
          changed = true;
        }
      }

      if (
        growingLeft &&
        verticalOverlap &&
        obs.x + obs.width <= anchorRight &&
        obstacleBlocksLeftGrowth(obs)
      ) {
        const nextX = Math.max(rect.x, obs.x + obs.width);
        const nextWidth = Math.max(MIN_RECT_SIZE, anchorRight - nextX);
        if (nextX !== rect.x || nextWidth !== rect.width) {
          rect.x = nextX;
          rect.width = nextWidth;
          changed = true;
        }
      }

      if (
        growingBottom &&
        horizontalOverlap &&
        obstacleBlocksBottomGrowth(obs)
      ) {
        const maxHeight = Math.max(MIN_RECT_SIZE, obs.y - rect.y);
        if (rect.height > maxHeight) {
          rect.height = maxHeight;
          changed = true;
        }
      }

      if (
        growingTop &&
        horizontalOverlap &&
        obs.y + obs.height <= anchorBottom &&
        obstacleBlocksTopGrowth(obs)
      ) {
        const nextY = Math.max(rect.y, obs.y + obs.height);
        const nextHeight = Math.max(MIN_RECT_SIZE, anchorBottom - nextY);
        if (nextY !== rect.y || nextHeight !== rect.height) {
          rect.y = nextY;
          rect.height = nextHeight;
          changed = true;
        }
      }
    });

    return changed;
  };

  let iterations = 0;
  while (applyObstacleLimits() && iterations < obstacles.length + 2) {
    iterations += 1;
  }

  rect = clampHitAreaRectToImage(rect, imageWidth, imageHeight);

  if (rectOverlapsAnyObstacle(rect, obstacles)) {
    return (
      constrainRectToNonOverlapByInterpolation(rect, fallback, obstacles) || fallback
    );
  }

  return normalizeConstrainedRect(rect);
};

/** Keep origin fixed while growing width/height toward targets without overlapping obstacles. */
export const constrainRectSizeByInterpolation = (
  originRect,
  currentSize,
  targetSize,
  obstacles = [],
  imageWidth,
  imageHeight
) => {
  const x = roundToInt(originRect?.x ?? 0);
  const y = roundToInt(originRect?.y ?? 0);
  const fallbackRect = clampHitAreaRectToImage(
    {
      x,
      y,
      width: currentSize?.width ?? MIN_RECT_SIZE,
      height: currentSize?.height ?? MIN_RECT_SIZE,
    },
    imageWidth,
    imageHeight
  );
  const targetRect = clampHitAreaRectToImage(
    {
      x,
      y,
      width: targetSize?.width ?? MIN_RECT_SIZE,
      height: targetSize?.height ?? MIN_RECT_SIZE,
    },
    imageWidth,
    imageHeight
  );

  return (
    constrainRectToNonOverlapByInterpolation(targetRect, fallbackRect, obstacles) ||
    fallbackRect
  );
};

export const getHitAreaDefaultName = (labelIndex = 1) =>
  `Hit Area ${Math.max(1, Math.floor(Number(labelIndex) || 1))}`;

export const isDefaultHitAreaName = (name) =>
  /^Hit Area\s+\d+$/i.test(String(name || "").trim());

export const resolveHitAreaDefaultLabelIndex = (item) => {
  const explicitIndex = Number(item?.defaultLabelIndex);
  if (Number.isFinite(explicitIndex) && explicitIndex > 0) {
    return Math.floor(explicitIndex);
  }
  const match = /^Hit Area\s+(\d+)$/i.exec(String(item?.name || "").trim());
  if (match) {
    const parsedIndex = Number(match[1]);
    if (Number.isFinite(parsedIndex) && parsedIndex > 0) {
      return Math.floor(parsedIndex);
    }
  }
  return 0;
};

export const getDefaultHitAreaLabelIndex = (item, fallbackIndex = 1) => {
  const resolvedIndex = resolveHitAreaDefaultLabelIndex(item);
  return resolvedIndex > 0 ? resolvedIndex : fallbackIndex;
};

export const resolveHitAreaDisplayName = (item, fallbackLabelIndex = 1) => {
  const trimmedName = String(item?.name ?? "").trim();
  if (trimmedName) return trimmedName;
  const labelIndex = resolveHitAreaDefaultLabelIndex(item);
  return labelIndex > 0
    ? getHitAreaDefaultName(labelIndex)
    : getHitAreaDefaultName(fallbackLabelIndex);
};

export const getNextHitAreaLabelIndex = (existingAreas = []) => {
  let maxIndex = 0;
  existingAreas.forEach((item) => {
    const labelIndex = resolveHitAreaDefaultLabelIndex(item);
    if (labelIndex > maxIndex) {
      maxIndex = labelIndex;
    }
  });
  return maxIndex + 1;
};

export const sanitizeHitAreaRenameInput = (
  rawValue,
  maxLength = MAX_HIT_AREA_NAME_LENGTH
) => {
  let value = String(rawValue ?? "").slice(0, maxLength);
  value = value.replace(/^\s+/, "");
  value = value.replace(/ {2,}/g, " ");
  return value;
};

export const shouldBlockHitAreaRenameSpaceKey = (
  value,
  selectionStart,
  selectionEnd
) => {
  const text = String(value ?? "");
  const start = Math.max(0, Number(selectionStart) || 0);
  const end = Math.max(start, Number(selectionEnd) || start);

  if (text.length === 0) {
    return true;
  }

  const charBefore = start > 0 ? text[start - 1] : "";
  const charAfter = end < text.length ? text[end] : "";
  return charBefore === " " || charAfter === " ";
};

export const validateHitAreaRename = ({
  rawName,
  existingAreas = [],
  editingHitAreaId = null,
  maxLength = MAX_HIT_AREA_NAME_LENGTH,
}) => {
  const nextName = String(rawName ?? "").trim().slice(0, maxLength);
  if (!nextName) {
    return { isValid: false, reason: "empty", normalizedName: "" };
  }

  const isDuplicate = existingAreas.some((item) => {
    if (String(item?.id) === String(editingHitAreaId)) return false;
    return (
      String(item?.name ?? "").trim().toLowerCase() === nextName.toLowerCase()
    );
  });

  if (isDuplicate) {
    return { isValid: false, reason: "duplicate", normalizedName: nextName };
  }

  return { isValid: true, reason: "", normalizedName: nextName };
};

export const normalizeHitAreaUrl = (rawValue) => {
  const trimmedValue = String(rawValue || "").trim();
  return isDefaultHitAreaUrlValue(trimmedValue) ? "" : trimmedValue;
};

export const normalizeAndValidateHttpUrl = (rawValue) => ({
  isValid: true,
  normalized: normalizeHitAreaUrl(rawValue),
  error: "",
});

export const isDefaultHitAreaUrlValue = (value) => {
  const normalizedValue = String(value || "").trim().toLowerCase();
  return (
    !normalizedValue ||
    normalizedValue === DEFAULT_HIT_AREA_URL_INPUT.toLowerCase() ||
    normalizedValue === "http://"
  );
};

export const getHitAreaLinkInputSeed = (targetLinkValue) => {
  const linkValue = String(targetLinkValue ?? "");
  return isDefaultHitAreaUrlValue(linkValue)
    ? DEFAULT_HIT_AREA_URL_INPUT
    : linkValue;
};

export const getConfiguredClickableAreaCount = (hitAreas = []) => {
  if (!Array.isArray(hitAreas)) return 0;
  return hitAreas.filter(
    (hitAreaItem) => !isDefaultHitAreaUrlValue(hitAreaItem?.targetLink)
  ).length;
};

export const getClickableAreasConfiguredTooltip = (count = 0) =>
  count > 0 ? `${count} clickable areas configured` : "";

const collectHitAreaIdsFromLayers = (layers = []) => {
  const ids = new Set();
  layers.forEach((layer) => {
    if (layer?.componentType !== "Image") return;
    (layer?.componentStyles?.hitAreas || []).forEach((hitArea) => {
      if (hitArea?.id) ids.add(hitArea.id);
    });
  });
  return ids;
};

/** Preserve current hit area data before editor undo/redo restores a snapshot. */
export const captureHitAreaEditorState = (
  layerCompData = [],
  animationObject = {}
) => {
  const hitAreasByImageId = {};

  layerCompData.forEach((layer) => {
    if (layer?.componentType !== "Image" || !layer?.id) return;
    hitAreasByImageId[layer.id] = {
      hitAreas: JSON.parse(
        JSON.stringify(layer.componentStyles?.hitAreas || [])
      ),
      hitAreaSequence: layer.componentStyles?.hitAreaSequence,
    };
  });

  const hitAreaAnimations = {};
  collectHitAreaIdsFromLayers(layerCompData).forEach((id) => {
    if (animationObject?.[id]) {
      hitAreaAnimations[id] = JSON.parse(JSON.stringify(animationObject[id]));
    }
  });

  return { hitAreasByImageId, hitAreaAnimations };
};

/** Re-apply preserved hit area data after editor undo/redo. */
export const mergeHitAreaEditorState = (
  layerCompData = [],
  animationObject = {},
  preservedState = { hitAreasByImageId: {}, hitAreaAnimations: {} }
) => {
  const nextLayerCompData = JSON.parse(JSON.stringify(layerCompData || []));
  const nextAnimationObject = JSON.parse(JSON.stringify(animationObject || {}));

  collectHitAreaIdsFromLayers(nextLayerCompData).forEach((id) => {
    delete nextAnimationObject[id];
  });

  nextLayerCompData.forEach((layer) => {
    if (layer?.componentType !== "Image" || !layer?.id) return;
    const preserved = preservedState.hitAreasByImageId?.[layer.id];
    if (!preserved) return;
    layer.componentStyles = {
      ...layer.componentStyles,
      hitAreas: preserved.hitAreas,
      hitAreaSequence: preserved.hitAreaSequence,
    };
  });

  Object.entries(preservedState.hitAreaAnimations || {}).forEach(
    ([id, animation]) => {
      nextAnimationObject[id] = animation;
    }
  );

  return {
    layerCompData: nextLayerCompData,
    animationObject: nextAnimationObject,
  };
};

/** Remove hit area data from editor undo snapshots. */
export const stripHitAreasForUndoSnapshot = (
  layerCompData = [],
  animationObject = {}
) => {
  const strippedLayers = JSON.parse(JSON.stringify(layerCompData || []));
  strippedLayers.forEach((layer) => {
    if (layer?.componentType !== "Image" || !layer?.componentStyles) return;
    layer.componentStyles.hitAreas = [];
    delete layer.componentStyles.hitAreaSequence;
  });

  const strippedAnimation = JSON.parse(JSON.stringify(animationObject || {}));
  collectHitAreaIdsFromLayers(layerCompData).forEach((id) => {
    delete strippedAnimation[id];
  });

  return {
    layerCompData: strippedLayers,
    animationObject: strippedAnimation,
  };
};

export const estimateHitAreaLinkPanelHeight = ({
  linkText = "",
  panelWidth = 240,
  isEditing = false,
  hasError = false,
}) => {
  const errorBlockHeight = hasError ? 20 : 0;

  if (isEditing) {
    return 40 + errorBlockHeight;
  }

  const editButtonWidth = 62;
  const horizontalPadding = 24;
  const textAreaWidth = Math.max(80, panelWidth - editButtonWidth - horizontalPadding);
  const averageCharWidth = 6.5;
  const lineHeight = 16.2;
  const verticalPadding = 8;
  const normalizedText = String(linkText || " ").trim() || " ";
  const lineCount = Math.max(
    1,
    Math.ceil(normalizedText.length / Math.max(8, Math.floor(textAreaWidth / averageCharWidth)))
  );
  const contentHeight = verticalPadding + (lineCount - 1) * lineHeight + 16;

  return Math.max(40, Math.ceil(contentHeight)) + errorBlockHeight;
};

export const resolveHitAreaLinkPanelPosition = ({
  hitAreaRect,
  panelWidth,
  panelHeight,
  panelGap = 8,
  containerWidth,
  containerHeight,
  edgeGap = HIT_AREA_LINK_PANEL_MIN_EDGE_GAP,
}) => {
  const hitArea = normalizeRect(hitAreaRect);
  const safePanelWidth = Math.max(1, toNumber(panelWidth, 1));
  const safePanelHeight = Math.max(1, toNumber(panelHeight, 1));
  const safeContainerWidth = Math.max(
    safePanelWidth + edgeGap * 2,
    toNumber(
      containerWidth,
      hitArea.x + hitArea.width + safePanelWidth + edgeGap
    )
  );
  const safeContainerHeight = Math.max(
    safePanelHeight + edgeGap * 2,
    toNumber(
      containerHeight,
      hitArea.y + hitArea.height + safePanelHeight + edgeGap
    )
  );
  const hitAreaLeft = hitArea.x;
  const hitAreaTop = hitArea.y;
  const hitAreaRight = hitArea.x + hitArea.width;
  const hitAreaBottom = hitArea.y + hitArea.height;

  const clampLeft = (left) =>
    clamp(
      left,
      edgeGap,
      Math.max(edgeGap, safeContainerWidth - safePanelWidth - edgeGap)
    );

  const clampTop = (top) =>
    clamp(
      top,
      edgeGap,
      Math.max(edgeGap, safeContainerHeight - safePanelHeight - edgeGap)
    );

  const panelRect = (left, top) => ({
    x: left,
    y: top,
    width: safePanelWidth,
    height: safePanelHeight,
  });

  const overlapsHitArea = (left, top) =>
    rectanglesOverlap(panelRect(left, top), hitArea);

  const rawCandidates = [
    {
      left: hitAreaRight - safePanelWidth,
      top: hitAreaTop - safePanelHeight - panelGap,
    },
    {
      left: hitAreaLeft,
      top: hitAreaTop - safePanelHeight - panelGap,
    },
    {
      left: hitAreaRight + panelGap,
      top: hitAreaTop,
    },
    {
      left: hitAreaLeft - safePanelWidth - panelGap,
      top: hitAreaTop,
    },
    {
      left: hitAreaLeft,
      top: hitAreaBottom + panelGap,
    },
    {
      left: hitAreaRight - safePanelWidth,
      top: hitAreaBottom + panelGap,
    },
    {
      left: hitAreaRight - safePanelWidth,
      top: hitAreaTop - safePanelHeight - HIT_AREA_LINK_PANEL_TOP_OFFSET_PX,
    },
  ];

  const clampedCandidates = rawCandidates.map((candidate) => ({
    left: clampLeft(candidate.left),
    top: clampTop(candidate.top),
  }));

  const nonOverlappingCandidate = clampedCandidates.find(
    (candidate) => !overlapsHitArea(candidate.left, candidate.top)
  );

  const resolved =
    nonOverlappingCandidate || {
      left: clampLeft((safeContainerWidth - safePanelWidth) / 2),
      top: edgeGap,
    };

  return {
    left: resolved.left,
    top: resolved.top,
    width: safePanelWidth,
  };
};

const makeHitAreaAnimationEntry = (overrides = {}) => ({
  delay: 0,
  duration: 0,
  end: 0,
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  rotation: 0,
  rotationX: 0,
  rotationY: 0,
  ease: "NONE",
  transform: 1,
  opacity: 1,
  translateX: 0,
  translateY: 0,
  transformOriginX: 0,
  transformOriginY: 0,
  transformOriginZ: 0,
  ...overrides,
});

const getHitAreaKeyframeX = (keyframe, hitArea) =>
  keyframe?.x !== undefined ? keyframe.x : hitArea?.x || 0;

const getHitAreaKeyframeY = (keyframe, hitArea) =>
  keyframe?.y !== undefined ? keyframe.y : hitArea?.y || 0;

const getHitAreaKeyframeWidth = (keyframe, hitArea) =>
  keyframe?.width !== undefined ? keyframe.width : hitArea?.width || 0;

const getHitAreaKeyframeHeight = (keyframe, hitArea) =>
  keyframe?.height !== undefined ? keyframe.height : hitArea?.height || 0;

const withHitAreaAnimationState = (keyframe, hitArea, overrides = {}) => {
  const x = getHitAreaKeyframeX(keyframe, hitArea);
  const y = getHitAreaKeyframeY(keyframe, hitArea);

  return makeHitAreaAnimationEntry({
    x,
    y,
    left: x,
    top: y,
    width: getHitAreaKeyframeWidth(keyframe, hitArea),
    height: getHitAreaKeyframeHeight(keyframe, hitArea),
    ...overrides,
  });
};

/** Keyframes for GSAP/export; falls back to the hit area root rect when none exist. */
export const getHitAreaKeyframesForAnimation = (hitArea) => {
  const keyframes = sortKeyframesByTime(
    [...(hitArea?.keyframes || [])].filter(
      (kf) => typeof kf?.time === "number" && Number.isFinite(kf.time)
    )
  );

  if (keyframes.length) return keyframes;

  const timelineStart = Number(hitArea?.timelineStart) || 0;
  return [
    {
      id: `${hitArea?.id || "hit-area"}-kf-start`,
      time: timelineStart,
      x: hitArea?.x,
      y: hitArea?.y,
      width: hitArea?.width,
      height: hitArea?.height,
    },
  ];
};

/**
 * Builds a standard animationObject.animation array for a hit area overlay,
 * using the same entry shape as Image/Text components (x, y, width, height,
 * opacity, transform, delay, duration, ease).
 */
export const buildHitAreaAnimationArray = (hitArea, keyframes = []) => {
  if (!Array.isArray(keyframes) || !keyframes.length) return [];

  const sortedKeyframes = sortKeyframesByTime(keyframes);
  const kf0 = sortedKeyframes[0];
  const timelineStart = Number(hitArea?.timelineStart) || 0;
  const timelineEnd = Number(hitArea?.timelineEnd) || HIT_AREA_MODAL_TIMELINE_DURATION;
  const entries = [];

  entries.push(
    withHitAreaAnimationState(kf0, hitArea, {
      delay: 0,
      duration: 0,
      end: 0,
      opacity: 0,
      ease: "NONE",
    })
  );

  entries.push(
    withHitAreaAnimationState(kf0, hitArea, {
      delay: timelineStart,
      duration: 0,
      end: timelineStart,
      opacity: 1,
      ease: "NONE",
    })
  );

  for (let index = 0; index < sortedKeyframes.length - 1; index += 1) {
    const keyframeA = sortedKeyframes[index];
    const keyframeB = sortedKeyframes[index + 1];
    entries.push(
      withHitAreaAnimationState(keyframeB, hitArea, {
        delay: keyframeA.time,
        duration: Math.max(0, keyframeB.time - keyframeA.time),
        end: keyframeB.time,
        opacity: 1,
        ease: "linear",
      })
    );
  }

  const lastKeyframe = sortedKeyframes[sortedKeyframes.length - 1];
  entries.push(
    withHitAreaAnimationState(lastKeyframe, hitArea, {
      delay: timelineEnd,
      duration: 0,
      end: timelineEnd,
      opacity: 0,
      ease: "NONE",
    })
  );

  return entries;
};

/** Rebuild animationObject entries for the given hit areas. */
export const rebuildHitAreaAnimationEntries = (
  animationObject,
  hitAreas = []
) => {
  const nextAnimationObject = { ...(animationObject || {}) };
  hitAreas.forEach((hitArea) => {
    if (!hitArea?.id) return;
    if (hitArea.hideBoolean) {
      delete nextAnimationObject[hitArea.id];
      return;
    }
    nextAnimationObject[hitArea.id] = {
      animation: buildHitAreaAnimationArray(
        hitArea,
        getHitAreaKeyframesForAnimation(hitArea)
      ),
    };
  });
  return nextAnimationObject;
};

/** Fills missing hit-area animation entries from layerCompData (e.g. server load). */
export const ensureHitAreaAnimationEntries = (animationObjectNew, layerCompData) => {
  if (!animationObjectNew || !Array.isArray(layerCompData)) return;

  layerCompData.forEach((layer) => {
    if (layer?.componentType !== "Image") return;

    const hitAreas = layer.componentStyles?.hitAreas;
    if (!Array.isArray(hitAreas) || !hitAreas.length) return;

    hitAreas.forEach((hitArea) => {
      if (hitArea?.hideBoolean || !hitArea?.id) return;

      const keyframes = getHitAreaKeyframesForAnimation(hitArea);

      // Always rebuild from layerCompData so every hit area stays in sync,
      // including when multiple hit areas exist on the same image.
      animationObjectNew[hitArea.id] = {
        animation: buildHitAreaAnimationArray(hitArea, keyframes),
      };
    });
  });
};
